'use client';

/**
 * CosmicTyper — embeddable React typing game for grades 3-5.
 *
 * Drop this file into a monorepo package (or app) and import it:
 *
 *   import CosmicTyper from "./CosmicTyper.jsx";
 *
 *   <div style={{ width: "100%", height: "100vh" }}>
 *     <CosmicTyper
 *       words={["spelling", "homework", "vocabulary"]}
 *       grade={4}
 *       onGameOver={(result) => console.log(result)}
 *     />
 *   </div>
 *
 * Requirements: React 18+. No other runtime deps.
 *
 * The component fills its parent (which must have a positioned, sized layout —
 * use width/height on the wrapper). All styles are scoped under
 * `.cosmic-typer-root` so they won't leak into your app's CSS.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// =============================================================================
// WORD LISTS
// =============================================================================
const WORDS_BY_GRADE = {
  3: [
    'cat',
    'dog',
    'sun',
    'run',
    'jump',
    'play',
    'book',
    'tree',
    'fish',
    'bird',
    'frog',
    'star',
    'moon',
    'fast',
    'slow',
    'ball',
    'game',
    'milk',
    'ride',
    'kite',
    'snow',
    'bear',
    'duck',
    'wing',
    'kind',
    'gold',
    'rock',
    'sand',
    'wave',
    'hill',
    'leaf',
    'seed',
    'king',
    'ship',
    'camp',
    'road',
    'baby',
    'cake',
    'park',
    'love',
    'warm',
    'cold',
    'food',
    'help',
    'rain',
    'sing',
    'read',
    'talk',
    'walk',
    'grow',
  ],
  4: [
    'pencil',
    'school',
    'family',
    'animal',
    'planet',
    'garden',
    'friend',
    'rocket',
    'castle',
    'dragon',
    'forest',
    'monkey',
    'rabbit',
    'banana',
    'window',
    'orange',
    'purple',
    'circle',
    'summer',
    'winter',
    'jacket',
    'yellow',
    'silver',
    'bright',
    'happy',
    'kitten',
    'puppy',
    'robot',
    'tiger',
    'zebra',
    'ocean',
    'island',
    'bridge',
    'button',
    'cookie',
    'pillow',
    'engine',
    'wonder',
    'frozen',
    'brave',
    'candle',
    'feather',
    'hidden',
    'jungle',
    'lantern',
    'muffin',
    'ribbon',
    'voyage',
    'puzzle',
    'mighty',
  ],
  5: [
    'adventure',
    'beautiful',
    'knowledge',
    'important',
    'mountain',
    'dinosaur',
    'butterfly',
    'chocolate',
    'wonderful',
    'incredible',
    'celebrate',
    'discovery',
    'fantastic',
    'champion',
    'galaxy',
    'electric',
    'invisible',
    'astronaut',
    'volcano',
    'telescope',
    'creature',
    'mystery',
    'journey',
    'explorer',
    'treasure',
    'laboratory',
    'pyramid',
    'skeleton',
    'tornado',
    'pumpkin',
    'elephant',
    'kangaroo',
    'crocodile',
    'umbrella',
    'helicopter',
    'triangle',
    'magnetic',
    'calendar',
    'library',
    'awesome',
    'blizzard',
    'cathedral',
    'diamond',
    'festival',
    'glacier',
    'hurricane',
    'moonlight',
    'nebula',
    'orchestra',
    'platypus',
  ],
};

const GRADE_INFO = {
  3: { emoji: '🐣', title: 'GRADE 3', desc: 'Short words, slow speed. A friendly start.' },
  4: { emoji: '🚀', title: 'GRADE 4', desc: "Bigger words, more action. You've got this!" },
  5: {
    emoji: '🌌',
    title: 'GRADE 5',
    desc: 'Fast asteroids that keep speeding up. Cosmic challenge!',
  },
};

const GRADE_DIFFICULTY = {
  3: { speedMul: 1.0, spawnDivisor: 1.0, timeRampPer60s: 0.15, spawnRampPer60s: 0.08 },
  4: { speedMul: 1.35, spawnDivisor: 1.2, timeRampPer60s: 0.22, spawnRampPer60s: 0.12 },
  5: { speedMul: 1.85, spawnDivisor: 1.5, timeRampPer60s: 0.35, spawnRampPer60s: 0.18 },
};

const ACHIEVEMENTS = [
  { combo: 5, title: 'ON FIRE!', msg: '5-word streak — keep it up!' },
  { combo: 10, title: 'STELLAR!', msg: '10 in a row — incredible!' },
  { combo: 20, title: 'SUPERNOVA!', msg: "20 streak! You're unstoppable!" },
  { combo: 30, title: 'GALAXY MASTER', msg: '30 streak! Legendary!' },
];

const ASTEROID_PALETTES = [
  { base: '#7a4a2b', crater: '#3d2615', hi: '#c98a5c' },
  { base: '#6b3a8a', crater: '#3a1d4d', hi: '#a566cc' },
  { base: '#2b6a7a', crater: '#143540', hi: '#56b8cc' },
  { base: '#a83a3a', crater: '#5a1b1b', hi: '#e07070' },
  { base: '#3a6a2b', crater: '#1c3414', hi: '#7ac654' },
];

// =============================================================================
// HELPERS
// =============================================================================
function shuffled(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function sanitizeWords(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((w) => (typeof w === 'string' ? w.trim().toLowerCase() : ''))
    .filter((w) => /^[a-z]+$/.test(w));
}

// =============================================================================
// SOUND FX (Web Audio synth, lazily initialized on first user gesture)
// =============================================================================
function makeSoundFX() {
  let ctx = null;
  let muted = false;
  function get() {
    if (!ctx) {
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        ctx = null;
      }
    }
    return ctx;
  }
  function tone(freq, dur = 0.08, type = 'square', vol = 0.08) {
    if (muted) return;
    const c = get();
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.value = vol;
    osc.connect(g);
    g.connect(c.destination);
    const t = c.currentTime;
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.start(t);
    osc.stop(t + dur);
  }
  return {
    keyHit: () => tone(880, 0.04, 'square', 0.04),
    keyMiss: () => tone(140, 0.1, 'sawtooth', 0.06),
    explode: () => {
      tone(220, 0.15, 'triangle', 0.1);
      setTimeout(() => tone(110, 0.2, 'sawtooth', 0.08), 50);
    },
    hurt: () => tone(90, 0.3, 'sawtooth', 0.1),
    levelUp: () => {
      [523, 659, 784, 1047].forEach((f, i) =>
        setTimeout(() => tone(f, 0.1, 'square', 0.07), i * 70),
      );
    },
    combo: () => tone(1320, 0.08, 'sine', 0.06),
    setMuted: (v) => {
      muted = v;
    },
  };
}

// =============================================================================
// SCOPED CSS — all rules live under `.cosmic-typer-root`
// =============================================================================
const COSMIC_CSS = `
.cosmic-typer-root {
  --ct-bg-deep: #05031a;
  --ct-bg-mid:  #1a0a3a;
  --ct-bg-top:  #2d0f5e;
  --ct-accent-cyan: #00f0ff;
  --ct-accent-pink: #ff2d92;
  --ct-accent-yellow: #ffd23f;
  --ct-accent-green: #a3ff5a;
  --ct-accent-red: #ff4d4d;
  --ct-text-soft: #e9e7ff;

  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: radial-gradient(ellipse at top, var(--ct-bg-top) 0%, var(--ct-bg-mid) 35%, var(--ct-bg-deep) 100%);
  color: var(--ct-text-soft);
  font-family: 'Trebuchet MS', 'Comic Sans MS', sans-serif;
  user-select: none;
  -webkit-user-select: none;
}
.cosmic-typer-root *, .cosmic-typer-root *::before, .cosmic-typer-root *::after { box-sizing: border-box; }

.cosmic-typer-root .full { position: absolute; inset: 0; }
.cosmic-typer-root .center-col { display: flex; flex-direction: column; align-items: center; justify-content: center; }

@keyframes ct-float       { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes ct-spin-slow   { from { transform: rotate(0); } to { transform: rotate(360deg); } }
@keyframes ct-shake       { 0%,100% { transform: translate(0,0); } 20% { transform: translate(-6px,2px); } 40% { transform: translate(6px,-2px); } 60% { transform: translate(-4px,3px); } 80% { transform: translate(4px,-3px); } }
@keyframes ct-pop-in      { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); } }
@keyframes ct-rise-fade   { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-60px); opacity: 0; } }
@keyframes ct-streak      { 0% { transform: scale(0.5); opacity: 0; } 20% { transform: scale(1.3); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes ct-title-bob   { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-8px) rotate(1deg); } }
@keyframes ct-blink       { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }

.cosmic-typer-root .game-title {
  font-family: 'Impact', 'Trebuchet MS', sans-serif;
  font-size: clamp(48px, 9vw, 120px);
  font-weight: 900; letter-spacing: 4px;
  background: linear-gradient(180deg, #fff 0%, var(--ct-accent-cyan) 50%, var(--ct-accent-pink) 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(0,240,255,0.6), 0 0 60px rgba(255,45,146,0.4);
  animation: ct-title-bob 4s ease-in-out infinite;
  margin: 0; line-height: 0.95;
}
.cosmic-typer-root .subtitle {
  font-size: clamp(14px, 1.6vw, 22px);
  color: var(--ct-accent-yellow);
  letter-spacing: 6px; text-transform: uppercase; margin-top: 6px;
}

.cosmic-typer-root .panel {
  background: rgba(10,5,40,0.55); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  border: 2px solid rgba(0,240,255,0.35); border-radius: 20px;
  box-shadow: 0 0 40px rgba(0,240,255,0.15), inset 0 0 30px rgba(255,45,146,0.05);
}

.cosmic-typer-root .pill-btn {
  cursor: pointer; border: 2px solid rgba(255,255,255,0.25);
  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02));
  color: var(--ct-text-soft); border-radius: 999px; padding: 14px 28px;
  font-size: 18px; font-weight: bold; letter-spacing: 1px;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}
.cosmic-typer-root .pill-btn:hover { transform: translateY(-2px); border-color: var(--ct-accent-cyan); box-shadow: 0 6px 20px rgba(0,240,255,0.35); }
.cosmic-typer-root .pill-btn.selected { border-color: var(--ct-accent-yellow); background: linear-gradient(180deg, rgba(255,210,63,0.25), rgba(255,210,63,0.05)); color: #fff; box-shadow: 0 0 24px rgba(255,210,63,0.5); }

.cosmic-typer-root .big-btn {
  cursor: pointer; border: none; color: #0a0028;
  background: linear-gradient(180deg, var(--ct-accent-yellow) 0%, #ffae00 100%);
  border-radius: 999px; padding: 18px 50px; font-size: 26px; font-weight: 900;
  letter-spacing: 2px; text-transform: uppercase;
  box-shadow: 0 8px 0 #b06a00, 0 0 30px rgba(255,210,63,0.6);
  transition: transform 80ms ease, box-shadow 80ms ease;
}
.cosmic-typer-root .big-btn:hover { transform: translateY(-2px); }
.cosmic-typer-root .big-btn:active { transform: translateY(4px); box-shadow: 0 4px 0 #b06a00, 0 0 30px rgba(255,210,63,0.6); }

.cosmic-typer-root .hud {
  position: absolute; top: 0; left: 0; right: 0;
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 24px; z-index: 10; pointer-events: none;
}
.cosmic-typer-root .hud .stat {
  background: rgba(10,5,40,0.6); border: 2px solid rgba(0,240,255,0.4);
  border-radius: 14px; padding: 10px 16px; font-weight: bold;
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
}
.cosmic-typer-root .stat-label { font-size: 11px; letter-spacing: 2px; color: var(--ct-accent-cyan); text-transform: uppercase; }
.cosmic-typer-root .stat-value { font-size: 26px; line-height: 1; color: #fff; font-family: 'Courier New', monospace; }
.cosmic-typer-root .heart { font-size: 28px; filter: drop-shadow(0 0 6px #ff4d8d); margin-right: 4px; display: inline-block; }
.cosmic-typer-root .heart.lost { filter: grayscale(1) brightness(0.4); }

.cosmic-typer-root .asteroid { position: absolute; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; transition: filter 120ms ease; }
.cosmic-typer-root .asteroid.targeted { filter: drop-shadow(0 0 12px var(--ct-accent-yellow)) drop-shadow(0 0 24px var(--ct-accent-yellow)); }
.cosmic-typer-root .word-label {
  position: absolute; font-family: 'Courier New', monospace;
  font-weight: 900; font-size: 22px; letter-spacing: 1px; pointer-events: none;
  text-shadow: 2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000;
  white-space: nowrap;
}
.cosmic-typer-root .word-typed { color: var(--ct-accent-green); }
.cosmic-typer-root .word-rest  { color: #ffffff; }

.cosmic-typer-root .float-score {
  position: absolute; transform: translate(-50%, -50%);
  font-family: 'Impact', sans-serif; font-size: 36px; font-weight: 900;
  color: var(--ct-accent-yellow);
  text-shadow: 0 0 12px rgba(255,210,63,0.9), 2px 2px 0 #000;
  animation: ct-rise-fade 900ms ease-out forwards; pointer-events: none;
}

.cosmic-typer-root .combo-badge {
  position: absolute; top: 90px; left: 50%; transform: translateX(-50%);
  font-family: 'Impact', sans-serif; font-size: 38px; color: var(--ct-accent-pink);
  text-shadow: 0 0 16px var(--ct-accent-pink), 3px 3px 0 #000;
  animation: ct-streak 400ms ease-out; pointer-events: none;
}

.cosmic-typer-root .type-prompt {
  position: absolute; bottom: 28px; left: 50%; transform: translateX(-50%);
  font-family: 'Courier New', monospace; font-size: 30px; font-weight: bold;
  background: rgba(10,5,40,0.65); border: 2px solid var(--ct-accent-cyan);
  border-radius: 14px; padding: 10px 24px; min-width: 240px; text-align: center;
  backdrop-filter: blur(6px); box-shadow: 0 0 24px rgba(0,240,255,0.3);
}
.cosmic-typer-root .cursor { display: inline-block; width: 2px; height: 22px; background: var(--ct-accent-cyan); margin-left: 4px; vertical-align: middle; animation: ct-blink 1s steps(1) infinite; }

.cosmic-typer-root .planet-ground {
  position: absolute; bottom: 0; left: 0; right: 0; height: 80px;
  background: radial-gradient(ellipse at center top, #4a1f7c 0%, #2d0f5e 60%, transparent 100%);
  pointer-events: none;
}

.cosmic-typer-root .stat-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px dashed rgba(255,255,255,0.15); }
.cosmic-typer-root .stat-row:last-child { border-bottom: none; }
.cosmic-typer-root .stat-row .lbl { color: var(--ct-accent-cyan); font-weight: bold; letter-spacing: 1px; text-transform: uppercase; font-size: 14px; }
.cosmic-typer-root .stat-row .val { font-family: 'Courier New', monospace; font-size: 24px; font-weight: bold; color: #fff; }

.cosmic-typer-root .diff-card {
  cursor: pointer; width: 180px; padding: 18px; border-radius: 18px;
  background: rgba(10,5,40,0.55); border: 3px solid rgba(255,255,255,0.2);
  text-align: center; transition: all 150ms ease;
}
.cosmic-typer-root .diff-card:hover { transform: translateY(-4px); }
.cosmic-typer-root .diff-card.selected { border-color: var(--ct-accent-yellow); box-shadow: 0 0 24px rgba(255,210,63,0.5); transform: translateY(-4px); }
.cosmic-typer-root .diff-emoji { font-size: 56px; line-height: 1; margin-bottom: 6px; display: block; }
.cosmic-typer-root .diff-title { font-size: 22px; font-weight: 900; letter-spacing: 2px; }
.cosmic-typer-root .diff-desc { font-size: 12px; color: rgba(255,255,255,0.7); margin-top: 4px; line-height: 1.3; }

.cosmic-typer-root .particle { position: absolute; width: 8px; height: 8px; border-radius: 50%; pointer-events: none; }

.cosmic-typer-root .footer-hint { position: absolute; bottom: 12px; left: 0; right: 0; text-align: center; font-size: 13px; color: rgba(255,255,255,0.4); letter-spacing: 2px; }

.cosmic-typer-root .toast {
  position: absolute; top: 100px; right: 24px;
  background: linear-gradient(135deg, var(--ct-accent-pink), #6a0dad);
  border: 2px solid var(--ct-accent-yellow); border-radius: 12px;
  padding: 12px 18px; color: #fff; box-shadow: 0 0 24px rgba(255,45,146,0.5);
  animation: ct-pop-in 400ms ease-out; z-index: 20; max-width: 260px;
}
.cosmic-typer-root .toast .ttl { font-weight: 900; letter-spacing: 1px; font-size: 14px; color: var(--ct-accent-yellow); }
.cosmic-typer-root .toast .msg { font-size: 13px; margin-top: 2px; }
`;

// Inject the style sheet exactly once per page
function useScopedStylesheet() {
  useEffect(() => {
    const ID = 'cosmic-typer-stylesheet';
    if (document.getElementById(ID)) return;
    const tag = document.createElement('style');
    tag.id = ID;
    tag.textContent = COSMIC_CSS;
    document.head.appendChild(tag);
  }, []);
}

// =============================================================================
// STARFIELD — sized to the wrapper (not the viewport)
// =============================================================================
function Starfield({ intensity = 1 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const stars = [];
    const blobs = [];
    let w = 0,
      h = 0;

    function setup() {
      const parent = canvas.parentElement;
      const rect = parent
        ? parent.getBoundingClientRect()
        : { width: window.innerWidth, height: window.innerHeight };
      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      stars.length = 0;
      for (let i = 0; i < 80; i++)
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 0.8 + 0.2,
          speed: 0.05 + Math.random() * 0.1,
          tw: Math.random() * Math.PI * 2,
          layer: 0,
        });
      for (let i = 0; i < 180; i++)
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.6 + 0.4,
          speed: 0.2 + Math.random() * 0.5,
          tw: Math.random() * Math.PI * 2,
          layer: 1,
        });

      blobs.length = 0;
      const palette = ['#ff2d92', '#00f0ff', '#9c27b0', '#ffd23f'];
      for (let i = 0; i < 5; i++) {
        blobs.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 120 + Math.random() * 200,
          color: palette[i % palette.length],
          alpha: 0.06 + Math.random() * 0.07,
        });
      }
    }
    setup();

    // Re-setup if the wrapper resizes (e.g. host app resizes panel)
    const ro = new ResizeObserver(setup);
    if (canvas.parentElement) ro.observe(canvas.parentElement);

    function frame() {
      ctx.clearRect(0, 0, w, h);
      blobs.forEach((b) => {
        const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        g.addColorStop(
          0,
          b.color +
            Math.floor(b.alpha * 255)
              .toString(16)
              .padStart(2, '0'),
        );
        g.addColorStop(1, '#00000000');
        ctx.fillStyle = g;
        ctx.fillRect(b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
      });
      stars.forEach((s) => {
        s.y += s.speed * intensity;
        s.tw += 0.05;
        if (s.y > h + 4) {
          s.y = -4;
          s.x = Math.random() * w;
        }
        const flicker = 0.6 + Math.sin(s.tw) * 0.4;
        ctx.fillStyle = `rgba(255,255,255,${flicker * (s.layer === 1 ? 1 : 0.6)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(frame);
    }
    frame();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [intensity]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, display: 'block', zIndex: 0, pointerEvents: 'none' }}
    />
  );
}

// =============================================================================
// ASTEROID & EXPLOSION
// =============================================================================
function AsteroidSvg({ size, palette, seed, isTargeted }) {
  const craters = useMemo(() => {
    const rng = mulberry32(seed);
    const out = [];
    const n = 4 + Math.floor(rng() * 3);
    for (let i = 0; i < n; i++) {
      out.push({ cx: 30 + rng() * 40, cy: 30 + rng() * 40, r: 3 + rng() * 7 });
    }
    return out;
  }, [seed]);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ animation: 'ct-spin-slow 18s linear infinite' }}
    >
      <defs>
        <radialGradient id={`g${seed}`} cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor={palette.hi} />
          <stop offset="60%" stopColor={palette.base} />
          <stop offset="100%" stopColor={palette.crater} />
        </radialGradient>
      </defs>
      <circle
        cx="50"
        cy="50"
        r="44"
        fill={`url(#g${seed})`}
        stroke="#000"
        strokeOpacity="0.4"
        strokeWidth="1.5"
      />
      {craters.map((c, i) => (
        <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill={palette.crater} fillOpacity="0.85" />
      ))}
      {isTargeted && (
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="#ffd23f"
          strokeWidth="2.5"
          strokeDasharray="6 4"
          opacity="0.9"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
      )}
    </svg>
  );
}

function Explosion({ x, y, color, onDone }) {
  const [particles] = useState(() => {
    const arr = [];
    const N = 18;
    for (let i = 0; i < N; i++) {
      const angle = (Math.PI * 2 * i) / N + Math.random() * 0.4;
      const speed = 60 + Math.random() * 80;
      arr.push({
        dx: Math.cos(angle) * speed,
        dy: Math.sin(angle) * speed,
        size: 4 + Math.random() * 6,
        color: Math.random() < 0.5 ? color : '#ffd23f',
      });
    }
    return arr;
  });
  const idRef = useRef('p' + Math.random().toString(36).slice(2));

  useEffect(() => {
    const t = setTimeout(onDone, 700);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{ position: 'absolute', left: x, top: y, pointerEvents: 'none', zIndex: 5 }}>
      {particles.map((p, i) => (
        <div
          key={i}
          className="particle"
          style={{
            background: p.color,
            width: p.size,
            height: p.size,
            boxShadow: `0 0 12px ${p.color}`,
            transform: `translate(-50%, -50%)`,
            animation: `${idRef.current}-${i} 700ms ease-out forwards`,
          }}
        />
      ))}
      <style>
        {particles
          .map(
            (p, i) => `
        @keyframes ${idRef.current}-${i} {
          0%   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + ${p.dx}px), calc(-50% + ${p.dy}px)) scale(0.3); opacity: 0; }
        }
      `,
          )
          .join('')}
      </style>
    </div>
  );
}

// =============================================================================
// MENU
// =============================================================================
function MenuScreen({ onStart, highScores, muted, onToggleMute, customWordCount, defaultGrade }) {
  const [grade, setGrade] = useState(defaultGrade);
  return (
    <div className="full center-col" style={{ zIndex: 2 }}>
      <div style={{ textAlign: 'center', marginTop: '-2vh' }}>
        <h1 className="game-title">COSMIC TYPER</h1>
        <div className="subtitle">⭐ Academy of the Galaxy ⭐</div>
      </div>

      {customWordCount > 0 && (
        <div
          className="panel"
          style={{
            marginTop: 18,
            padding: '10px 18px',
            borderColor: 'var(--ct-accent-yellow)',
            boxShadow: '0 0 18px rgba(255,210,63,0.35)',
            fontSize: 14,
            letterSpacing: 1,
          }}
        >
          📚 <strong>Custom word pack loaded</strong> — {customWordCount} word
          {customWordCount === 1 ? '' : 's'} will play first, then more are generated to keep the
          game going.
        </div>
      )}

      <div className="panel" style={{ marginTop: 24, padding: 28, maxWidth: 720 }}>
        <div
          style={{
            textAlign: 'center',
            fontSize: 18,
            marginBottom: 16,
            color: 'var(--ct-accent-cyan)',
            letterSpacing: 2,
          }}
        >
          PICK YOUR MISSION LEVEL
        </div>
        <div style={{ display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[3, 4, 5].map((g) => {
            const info = GRADE_INFO[g];
            const hi = highScores[g] || 0;
            return (
              <div
                key={g}
                className={`diff-card${grade === g ? ' selected' : ''}`}
                onClick={() => setGrade(g)}
              >
                <span className="diff-emoji">{info.emoji}</span>
                <div className="diff-title">{info.title}</div>
                <div className="diff-desc">{info.desc}</div>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: 'var(--ct-accent-yellow)',
                    fontWeight: 700,
                  }}
                >
                  HIGH SCORE: {hi}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <button className="big-btn" onClick={() => onStart(grade)}>
            BLAST OFF!
          </button>
        </div>
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 18, alignItems: 'center' }}>
        <button
          className="pill-btn"
          onClick={onToggleMute}
          style={{ padding: '8px 18px', fontSize: 14 }}
        >
          {muted ? '🔇 SOUND OFF' : '🔊 SOUND ON'}
        </button>
      </div>

      <div className="footer-hint">
        TYPE THE WORDS ON FALLING ASTEROIDS BEFORE THEY HIT THE PLANET
      </div>
    </div>
  );
}

// =============================================================================
// GAME
// =============================================================================
function GameScreen({ grade, onGameOver, customWords, sound }) {
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [asteroids, setAsteroids] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [typed, setTyped] = useState('');
  const [explosions, setExplosions] = useState([]);
  const [floatScores, setFloatScores] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [comboFlash, setComboFlash] = useState(0);
  const [achievement, setAchievement] = useState(null);
  const [shaking, setShaking] = useState(false);

  const stats = useRef({
    startTime: Date.now(),
    correctChars: 0,
    wrongChars: 0,
    wordsTyped: 0,
    asteroidsHit: 0,
  });

  const customQueueRef = useRef([]);
  useEffect(() => {
    customQueueRef.current = customWords.length > 0 ? shuffled(customWords) : [];
  }, [customWords]);

  const asteroidsRef = useRef(asteroids);
  const activeIdRef = useRef(activeId);
  const typedRef = useRef(typed);
  const livesRef = useRef(lives);
  const levelRef = useRef(level);
  asteroidsRef.current = asteroids;
  activeIdRef.current = activeId;
  typedRef.current = typed;
  livesRef.current = lives;
  levelRef.current = level;

  const spawnTimerRef = useRef(0);

  // Track the wrapper's actual rendered size (works for both fullscreen and embedded layouts)
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    function update() {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.max(1, Math.floor(r.width)), h: Math.max(1, Math.floor(r.height)) });
    }
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Game loop
  useEffect(() => {
    let raf,
      last = performance.now();
    const list = WORDS_BY_GRADE[grade];
    const diff = GRADE_DIFFICULTY[grade];

    function elapsedMinutes() {
      return (Date.now() - stats.current.startTime) / 60000;
    }
    function timeSpeedMul() {
      return 1 + elapsedMinutes() * diff.timeRampPer60s;
    }
    function timeSpawnMul() {
      return Math.max(0.35, 1 - elapsedMinutes() * diff.spawnRampPer60s);
    }

    function nextWord() {
      if (customQueueRef.current.length > 0) return customQueueRef.current.shift();
      return list[Math.floor(Math.random() * list.length)];
    }

    function spawn() {
      const w = nextWord();
      const palette = ASTEROID_PALETTES[Math.floor(Math.random() * ASTEROID_PALETTES.length)];
      const x = 80 + Math.random() * Math.max(1, size.w - 160);
      const baseVy = 32 + Math.random() * 22;
      const vy = baseVy * diff.speedMul * timeSpeedMul() + levelRef.current * 5;
      const ast = {
        id: Math.random().toString(36).slice(2),
        word: w,
        x,
        y: -60,
        vy,
        size: Math.max(70, w.length * 12),
        palette,
        seed: Math.floor(Math.random() * 1e9),
      };
      setAsteroids((prev) => [...prev, ast]);
    }

    function tick(now) {
      const dt = Math.min(50, now - last) / 1000;
      last = now;

      spawnTimerRef.current -= dt;
      const baseInterval = Math.max(
        0.55,
        (2.2 - (levelRef.current - 1) * 0.18) / diff.spawnDivisor,
      );
      const interval = baseInterval * timeSpawnMul();
      if (spawnTimerRef.current <= 0) {
        spawn();
        spawnTimerRef.current = interval * (0.7 + Math.random() * 0.6);
      }

      setAsteroids((prev) => {
        const groundY = size.h - 90;
        const survivors = [];
        let lostLives = 0;
        for (const a of prev) {
          const ny = a.y + a.vy * dt;
          if (ny >= groundY) {
            lostLives += 1;
            if (a.id === activeIdRef.current) {
              setActiveId(null);
              setTyped('');
            }
          } else {
            survivors.push({ ...a, y: ny });
          }
        }
        if (lostLives > 0) {
          sound.hurt();
          setShaking(true);
          setTimeout(() => setShaking(false), 350);
          setLives((L) => Math.max(0, L - lostLives));
          setCombo(0);
        }
        return survivors;
      });

      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [grade, size.w, size.h, sound]);

  // Game over
  useEffect(() => {
    if (lives <= 0) {
      const elapsedMin = Math.max(0.0167, (Date.now() - stats.current.startTime) / 60000);
      const wpm = Math.round(stats.current.wordsTyped / elapsedMin);
      const totalChars = stats.current.correctChars + stats.current.wrongChars;
      const acc =
        totalChars === 0 ? 100 : Math.round((stats.current.correctChars / totalChars) * 100);
      onGameOver({
        score,
        level,
        bestCombo,
        wpm,
        accuracy: acc,
        wordsTyped: stats.current.wordsTyped,
        grade,
      });
    }
  }, [lives, score, level, bestCombo, onGameOver, grade]);

  // Level-up
  useEffect(() => {
    const expected = Math.floor(stats.current.asteroidsHit / 10) + 1;
    if (expected > levelRef.current) {
      setLevel(expected);
      sound.levelUp();
      pushAchievement({ title: `LEVEL ${expected}!`, msg: 'Speed and spawn rate up.' });
    }
  }, [score, sound]);

  // Keyboard
  useEffect(() => {
    function onKey(e) {
      if (e.key.length !== 1) return;
      const ch = e.key.toLowerCase();
      if (!/^[a-z]$/.test(ch)) return;
      e.preventDefault();

      const list = asteroidsRef.current;
      let active = activeIdRef.current ? list.find((a) => a.id === activeIdRef.current) : null;

      if (!active) {
        const candidates = list.filter((a) => a.word[0] === ch);
        if (candidates.length === 0) {
          stats.current.wrongChars += 1;
          sound.keyMiss();
          setCombo(0);
          return;
        }
        candidates.sort((a, b) => b.y - a.y);
        active = candidates[0];
        setActiveId(active.id);
        setTyped(ch);
        stats.current.correctChars += 1;
        sound.keyHit();
        if (active.word.length === 1) destroyAsteroid(active);
        return;
      }

      const nextIdx = typedRef.current.length;
      const expected = active.word[nextIdx];
      if (ch === expected) {
        const newTyped = active.word.slice(0, nextIdx + 1);
        setTyped(newTyped);
        stats.current.correctChars += 1;
        sound.keyHit();
        if (newTyped.length === active.word.length) destroyAsteroid(active);
      } else {
        stats.current.wrongChars += 1;
        sound.keyMiss();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sound]);

  function pushAchievement(a) {
    setAchievement(a);
    setTimeout(() => setAchievement(null), 2200);
  }

  function destroyAsteroid(a) {
    sound.explode();
    stats.current.asteroidsHit += 1;
    stats.current.wordsTyped += 1;
    const points = a.word.length * 10 * (1 + Math.floor(combo / 5) * 0.5);
    const rounded = Math.round(points);
    setScore((s) => s + rounded);
    setCombo((c) => {
      const nc = c + 1;
      if (nc > bestCombo) setBestCombo(nc);
      const a2 = ACHIEVEMENTS.find((x) => x.combo === nc);
      if (a2) {
        pushAchievement(a2);
        sound.combo();
      }
      setComboFlash((f) => f + 1);
      return nc;
    });
    setExplosions((prev) => [
      ...prev,
      { id: Math.random().toString(36).slice(2), x: a.x, y: a.y, color: a.palette.hi },
    ]);
    setFloatScores((prev) => [
      ...prev,
      { id: Math.random().toString(36).slice(2), x: a.x, y: a.y, text: `+${rounded}` },
    ]);
    setAsteroids((prev) => prev.filter((x) => x.id !== a.id));
    setActiveId(null);
    setTyped('');
  }

  function removeExplosion(id) {
    setExplosions((prev) => prev.filter((e) => e.id !== id));
  }

  useEffect(() => {
    if (floatScores.length === 0) return;
    const t = setTimeout(() => setFloatScores((p) => p.slice(1)), 900);
    return () => clearTimeout(t);
  }, [floatScores]);

  const active = activeId ? asteroids.find((a) => a.id === activeId) : null;

  return (
    <div
      ref={wrapperRef}
      className="full"
      style={{ animation: shaking ? 'ct-shake 350ms' : 'none' }}
    >
      <Starfield intensity={1 + level * 0.15} />
      <div className="planet-ground" />

      <div className="hud">
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="stat">
            <div className="stat-label">SCORE</div>
            <div className="stat-value">{score}</div>
          </div>
          <div className="stat">
            <div className="stat-label">LEVEL</div>
            <div className="stat-value">{level}</div>
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">LIVES</div>
          <div style={{ marginTop: 2 }}>
            {[0, 1, 2].map((i) => (
              <span key={i} className={`heart${i < 3 - lives ? ' lost' : ''}`}>
                ♥
              </span>
            ))}
          </div>
        </div>
      </div>

      {combo >= 3 && (
        <div key={comboFlash} className="combo-badge">
          ✨ COMBO × {combo} ✨
        </div>
      )}
      {achievement && (
        <div className="toast">
          <div className="ttl">{achievement.title}</div>
          <div className="msg">{achievement.msg}</div>
        </div>
      )}

      {asteroids.map((a) => {
        const isActive = a.id === activeId;
        const wt = isActive ? typed : '';
        const wr = a.word.slice(wt.length);
        return (
          <div
            key={a.id}
            className={`asteroid${isActive ? ' targeted' : ''}`}
            style={{ left: a.x, top: a.y, width: a.size, height: a.size }}
          >
            <AsteroidSvg size={a.size} palette={a.palette} seed={a.seed} isTargeted={isActive} />
            <div className="word-label">
              <span className="word-typed">{wt}</span>
              <span className="word-rest">{wr}</span>
            </div>
          </div>
        );
      })}

      {explosions.map((e) => (
        <Explosion
          key={e.id}
          x={e.x}
          y={e.y}
          color={e.color}
          onDone={() => removeExplosion(e.id)}
        />
      ))}
      {floatScores.map((f) => (
        <div key={f.id} className="float-score" style={{ left: f.x, top: f.y }}>
          {f.text}
        </div>
      ))}

      <div className="type-prompt">
        {active ? (
          <>
            <span className="word-typed">{typed}</span>
            <span className="word-rest">{active.word.slice(typed.length)}</span>
            <span className="cursor"></span>
          </>
        ) : (
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>Type a letter to lock on…</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// GAME OVER
// =============================================================================
function GameOverScreen({ result, isHigh, onRetry, onMenu }) {
  return (
    <div className="full center-col" style={{ zIndex: 2 }}>
      <div className="panel" style={{ padding: 32, minWidth: 420, maxWidth: 540 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, animation: 'ct-float 2s ease-in-out infinite' }}>
            {isHigh ? '🏆' : '🚀'}
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: 3, color: '#fff' }}>
            {isHigh ? 'NEW HIGH SCORE!' : 'MISSION COMPLETE'}
          </div>
          <div
            style={{ fontSize: 14, color: 'var(--ct-accent-cyan)', letterSpacing: 4, marginTop: 4 }}
          >
            {GRADE_INFO[result.grade].title}
          </div>
        </div>
        <div style={{ marginTop: 22 }}>
          <div className="stat-row">
            <span className="lbl">Score</span>
            <span className="val">{result.score}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Words Typed</span>
            <span className="val">{result.wordsTyped}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Best Streak</span>
            <span className="val">{result.bestCombo}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Speed (WPM)</span>
            <span className="val">{result.wpm}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Accuracy</span>
            <span className="val">{result.accuracy}%</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Level Reached</span>
            <span className="val">{result.level}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 24, justifyContent: 'center' }}>
          <button className="pill-btn" onClick={onMenu}>
            ↩ MENU
          </button>
          <button className="big-btn" onClick={onRetry}>
            PLAY AGAIN
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PUBLIC COMPONENT
// =============================================================================
/**
 * <CosmicTyper />
 *
 * Props:
 *   words?: string[]                  // optional custom word list (used first)
 *   grade?: 3 | 4 | 5                 // default difficulty (default 4)
 *   onGameOver?: (result) => void     // fired when a run ends
 *                                     //   result = { score, level, bestCombo, wpm, accuracy, wordsTyped, grade }
 *   className?: string                // extra class on the root container
 *   style?: React.CSSProperties       // extra inline style on the root container
 */
export default function CosmicTyper({
  words = [],
  grade: gradeProp = 4,
  onGameOver,
  className = '',
  style,
}) {
  useScopedStylesheet();

  const customWords = useMemo(() => sanitizeWords(words), [words]);
  const sound = useMemo(() => makeSoundFX(), []);
  const validGrade = [3, 4, 5].includes(gradeProp) ? gradeProp : 4;

  const [screen, setScreen] = useState('menu');
  const [grade, setGrade] = useState(validGrade);
  const [result, setResult] = useState(null);
  const [highScores, setHighScores] = useState({ 3: 0, 4: 0, 5: 0 });
  const [muted, setMuted] = useState(false);
  const [isHigh, setIsHigh] = useState(false);

  function handleStart(g) {
    setGrade(g);
    setResult(null);
    setIsHigh(false);
    setScreen('game');
  }

  const handleGameOver = useCallback(
    (r) => {
      setResult(r);
      const prev = highScores[r.grade] || 0;
      if (r.score > prev) {
        setHighScores((h) => ({ ...h, [r.grade]: r.score }));
        setIsHigh(true);
      } else {
        setIsHigh(false);
      }
      setScreen('over');
      if (onGameOver) onGameOver(r);
    },
    [highScores, onGameOver],
  );

  function toggleMute() {
    setMuted((m) => {
      sound.setMuted(!m);
      return !m;
    });
  }

  return (
    <div className={`cosmic-typer-root ${className}`} style={style}>
      {screen !== 'game' && <Starfield />}

      {screen === 'menu' && (
        <MenuScreen
          onStart={handleStart}
          highScores={highScores}
          muted={muted}
          onToggleMute={toggleMute}
          customWordCount={customWords.length}
          defaultGrade={grade}
        />
      )}
      {screen === 'game' && (
        <GameScreen
          grade={grade}
          onGameOver={handleGameOver}
          customWords={customWords}
          sound={sound}
        />
      )}
      {screen === 'over' && result && (
        <GameOverScreen
          result={result}
          isHigh={isHigh}
          onRetry={() => handleStart(result.grade)}
          onMenu={() => setScreen('menu')}
        />
      )}
    </div>
  );
}
